import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

// Mock Google Reviews data styled after real Google reviews
const googleReviews = [
  {
    id: 1,
    author: 'Ted G.',
    location: 'Melbourne, Florida',
    rating: 5,
    date: '2 months ago',
    text: 'Dr. Moyles helped to not only resolve our issue, but to explain, educate and make sure that we understood the workings of the issue. His follow-up afterwards was also greatly appreciated.',
    avatar: 'TG',
  },
  {
    id: 2,
    author: 'Sarah M.',
    location: 'Palm Bay, FL',
    rating: 5,
    date: '1 month ago',
    text: 'Exceptional care from start to finish. The staff was incredibly professional and Dr. Moyles took the time to thoroughly explain my carpal tunnel diagnosis and treatment options. My surgery was minimally invasive and recovery was faster than expected.',
    avatar: 'SM',
  },
  {
    id: 3,
    author: 'Michael R.',
    location: 'Melbourne, FL',
    rating: 5,
    date: '3 weeks ago',
    text: 'After suffering from trigger finger for months, I finally found relief at Melbourne Hand Center. The procedure was quick and virtually painless. Dr. Frye and his team are true professionals.',
    avatar: 'MR',
  },
  {
    id: 4,
    author: 'Jennifer L.',
    location: 'Viera, FL',
    rating: 5,
    date: '2 months ago',
    text: 'I was nervous about my wrist surgery, but the team at Melbourne Hand Center put me at ease. The telemedicine option was great for my follow-up appointments. Highly recommend!',
    avatar: 'JL',
  },
  {
    id: 5,
    author: 'Robert K.',
    location: 'Cocoa Beach, FL',
    rating: 5,
    date: '1 month ago',
    text: 'Best orthopedic experience I\'ve ever had. Clean, modern facility and incredibly knowledgeable doctors. They got me back to playing golf within weeks of my surgery.',
    avatar: 'RK',
  },
  {
    id: 6,
    author: 'Patricia D.',
    location: 'Rockledge, FL',
    rating: 5,
    date: '6 weeks ago',
    text: 'The care I received for my Dupuytren\'s contracture was outstanding. Dr. Moyles explained every step of the process and the results have been life-changing. I can finally open my hand fully again.',
    avatar: 'PD',
  },
];

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-5 h-5 ${
          star <= rating ? 'fill-warning text-warning' : 'fill-muted text-muted'
        }`}
      />
    ))}
  </div>
);

export const ReviewsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const reviewsPerPage = 3;
  const totalPages = Math.ceil(googleReviews.length / reviewsPerPage);

  const nextReviews = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevReviews = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const visibleReviews = googleReviews.slice(
    currentIndex * reviewsPerPage,
    (currentIndex + 1) * reviewsPerPage
  );

  const averageRating = (googleReviews.reduce((acc, r) => acc + r.rating, 0) / googleReviews.length).toFixed(1);

  return (
    <section id="reviews" className="py-20 lg:py-28 bg-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {/* Google Badge */}
          <div className="inline-flex items-center gap-3 bg-card border border-border shadow-card rounded-full px-6 py-3 mb-8">
            <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-semibold text-foreground">Google Reviews</span>
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 fill-warning text-warning" />
              <span className="font-bold text-foreground">{averageRating}</span>
            </div>
          </div>

          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            What Our Patients Say
          </h2>
          <p className="text-lg text-muted-foreground">
            Real reviews from real patients. See why Brevard County trusts 
            Melbourne Hand Center for their hand and upper extremity care.
          </p>
        </div>

        {/* Overall Rating Card */}
        <div className="bg-gradient-trust rounded-2xl p-8 mb-12 max-w-md mx-auto text-center shadow-card">
          <div className="flex justify-center mb-4">
            <StarRating rating={5} />
          </div>
          <p className="text-5xl font-bold text-foreground mb-2">{averageRating}</p>
          <p className="text-muted-foreground">Based on {googleReviews.length}+ Google Reviews</p>
          <a 
            href="https://www.google.com/search?q=melbourne+hand+center+reviews" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary font-semibold mt-4 hover:underline"
          >
            View on Google
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-8">
          {visibleReviews.map((review, index) => (
            <Card 
              key={review.id}
              className="bg-card border border-border shadow-card hover:shadow-lg transition-all duration-300 animate-fade-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardContent className="p-6 flex flex-col h-full">
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                    {review.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">{review.author}</p>
                    <p className="text-sm text-muted-foreground">{review.location}</p>
                  </div>
                  <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>

                {/* Rating & Date */}
                <div className="flex items-center gap-3 mb-4">
                  <StarRating rating={review.rating} />
                  <span className="text-sm text-muted-foreground">{review.date}</span>
                </div>

                {/* Review Text */}
                <div className="relative flex-grow">
                  <Quote className="absolute -top-2 -left-2 w-8 h-8 text-primary/10" />
                  <p className="text-foreground/90 relative z-10 pl-4">
                    {review.text}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={prevReviews}
            className="rounded-full"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex gap-2">
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentIndex 
                    ? 'bg-primary w-6' 
                    : 'bg-primary/30 hover:bg-primary/50'
                }`}
              />
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={nextReviews}
            className="rounded-full"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
